import ServerError from '#admin/components/ServerError.js';
import { useCSRFToken } from '#admin/contexts/CSRFContext.js';
import { ENV } from '#admin/services/env.server.js';
import { useApolloClient } from '@apollo/client/react';
import { withEmotionCache } from '@emotion/react';
import { dialogClasses, GlobalStyles } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import type { Route } from './+types/root.js';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';
export function loader() {
  return {
    ENV: {
      SENTRY_RELEASE: ENV.SENTRY_RELEASE,
      SENTRY_ENVIRONMENT: ENV.SENTRY_ENVIRONMENT,
      RESOURCES_PUBLIC_URL: ENV.RESOURCES_PUBLIC_URL,
    },
  };
}

declare global {
  interface Window {
    ENV: ReturnType<typeof loader>;
  }
}

export function clientLoader() {
  return window.ENV;
}

const App = withEmotionCache(function Document({ loaderData }: Route.ComponentProps, emotionCache) {
  const csrfToken = useCSRFToken();
  const apolloClient = useApolloClient();
  return (
    <html lang="en">
      <head suppressHydrationWarning={true}>
        {/* Try to fix hydration errors caused by emotion prepend */}
        {emotionCache.sheet.tags.map((s, i) => (
          <style
            key={i}
            data-emotion={s.dataset.emotion}
            data-s={s.dataset.s}
            nonce={s.nonce || undefined}
            dangerouslySetInnerHTML={{ __html: s.innerHTML }}
          />
        ))}
        <meta name="emotion-insertion-point" content="" data-first="true" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&family=Roboto+Flex:opsz,wght@8..144,100..1000&family=Roboto+Slab:wght@100..900&display=swap"
        />
        <meta name="csrf-token" content={csrfToken ?? ''} />
        <GlobalStyles
          styles={(theme) => ({
            [`.${dialogClasses.paper} > form`]: {
              marginBottom: 0,
            },
          })}
        />
        <Meta />
        <Links />
      </head>
      <body>
        <SnackbarProvider anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <NuqsAdapter>
            <Outlet />
          </NuqsAdapter>
        </SnackbarProvider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(loaderData.ENV)}; window.__apolloCache = ${JSON.stringify(apolloClient.extract())}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
});

export default App;

export const ErrorBoundary = ServerError;
